import React, { useEffect, useMemo, useState } from 'react';
import './HealthProgressGraphModal.css';

const METRICS = [
  { key: 'weight', label: 'Weight', unit: 'kg', color: '#2563eb', lowerIsBetter: true },
  { key: 'totalFatPercentage', label: 'Body fat', unit: '%', color: '#f97316', lowerIsBetter: true },
  { key: 'bmi', label: 'BMI', unit: '', color: '#8b5cf6', lowerIsBetter: true },
  { key: 'skeletalMusclePercentage', label: 'Skeletal muscle', unit: '%', color: '#10b981', lowerIsBetter: false },
  { key: 'visceralFat', label: 'Visceral fat', unit: '', color: '#ef4444', lowerIsBetter: true },
  { key: 'bmr', label: 'BMR', unit: 'kcal', color: '#0891b2', lowerIsBetter: null },
  { key: 'tsf', label: 'TSF', unit: '', color: '#db2777', lowerIsBetter: null },
  { key: 'metaAge', label: 'Metabolic age', unit: 'yrs', color: '#ca8a04', lowerIsBetter: true }
];

const toNumber = value => {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return null;
  const parsed = Number(value);
  // The health-reading API stores some unrecorded measurements as 0. None of
  // the graphed body measurements has a meaningful zero value, so exclude
  // non-positive values instead of drawing artificial drops to zero.
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const svgDate = value => {
  const date = new Date(`${value}T00:00:00`);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatValue = (value, unit = '') => `${Number(value).toFixed(1).replace(/\.0$/, '')}${unit ? ` ${unit}` : ''}`;

const buildPath = (points, x, y, width, height) => {
  if (points.length === 1) return `M ${x + width / 2} ${y + height / 2}`;
  const values = points.map(point => point.value);
  let min = Math.min(...values);
  let max = Math.max(...values);
  const padding = Math.max((max - min) * 0.15, Math.abs(max || 1) * 0.03);
  min -= padding;
  max += padding;
  return points.map((point, index) => {
    const px = x + (index / (points.length - 1)) * width;
    const py = y + height - ((point.value - min) / (max - min || 1)) * height;
    return `${index === 0 ? 'M' : 'L'} ${px.toFixed(1)} ${py.toFixed(1)}`;
  }).join(' ');
};

const HealthProgressGraphModal = ({ customer, companyInfo, readings, onClose }) => {
  const availableDates = useMemo(() => readings.map(item => item.readingDate).filter(Boolean).sort(), [readings]);
  const [startDate, setStartDate] = useState(availableDates[0] || '');
  const [endDate, setEndDate] = useState(availableDates[availableDates.length - 1] || '');
  const [selectedMetrics, setSelectedMetrics] = useState(() => METRICS.map(metric => metric.key));
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    const handleKeyDown = event => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const filteredReadings = useMemo(() => readings
    .filter(item => item.readingDate && (!startDate || item.readingDate >= startDate) && (!endDate || item.readingDate <= endDate))
    .sort((a, b) => a.readingDate.localeCompare(b.readingDate) || (a.id || 0) - (b.id || 0)), [readings, startDate, endDate]);

  const metricSeries = useMemo(() => METRICS.map(metric => ({
    ...metric,
    points: filteredReadings.map(item => ({ date: item.readingDate, value: toNumber(item[metric.key]) })).filter(item => item.value !== null)
  })).filter(metric => metric.points.length > 0), [filteredReadings]);

  const selectedMetricSeries = useMemo(() => metricSeries.filter(metric => selectedMetrics.includes(metric.key)), [metricSeries, selectedMetrics]);

  const toggleMetric = key => {
    setSelectedMetrics(current => current.includes(key)
      ? current.filter(metricKey => metricKey !== key)
      : [...current, key]);
  };

  const weightSeries = selectedMetricSeries.find(metric => metric.key === 'weight');
  const weightChange = weightSeries?.points.length > 1
    ? weightSeries.points[weightSeries.points.length - 1].value - weightSeries.points[0].value
    : null;

  const downloadGraph = () => {
    setDownloadError('');
    const svg = document.getElementById('health-progress-share-card');
    if (!svg) return;
    const serialized = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([serialized], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1350;
      const context = canvas.getContext('2d');
      context.fillStyle = '#f6f9ff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const link = document.createElement('a');
      link.download = `${(customer?.fullName || 'customer').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-health-progress.png`;
      link.href = canvas.toDataURL('image/png', 1);
      link.click();
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      setDownloadError('The image could not be created. Please try again.');
    };
    image.src = url;
  };

  const invalidRange = Boolean(startDate && endDate && startDate > endDate);

  return (
    <div className="health-graph-overlay" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <div className="health-graph-modal" role="dialog" aria-modal="true" aria-labelledby="health-graph-title">
        <div className="health-graph-heading">
          <div>
            <h2 id="health-graph-title">Health Progress Graph</h2>
            <p>Choose a date range, then download a portrait image ready to share.</p>
          </div>
          <button className="health-graph-close" onClick={onClose} aria-label="Close graph">×</button>
        </div>

        <div className="health-graph-toolbar">
          <label>From<input type="date" value={startDate} max={endDate || undefined} onChange={event => setStartDate(event.target.value)} /></label>
          <label>To<input type="date" value={endDate} min={startDate || undefined} onChange={event => setEndDate(event.target.value)} /></label>
          <details className="health-graph-metric-picker">
            <summary>Graphs ({selectedMetricSeries.length})</summary>
            <div className="health-graph-metric-options">
              {METRICS.map(metric => {
                const hasData = metricSeries.some(series => series.key === metric.key);
                return (
                  <label key={metric.key} className={!hasData ? 'unavailable' : ''}>
                    <input type="checkbox" checked={selectedMetrics.includes(metric.key) && hasData} disabled={!hasData} onChange={() => toggleMetric(metric.key)} />
                    <span style={{ '--metric-color': metric.color }}>{metric.label}</span>
                    {!hasData && <small>No data</small>}
                  </label>
                );
              })}
            </div>
          </details>
          <button className="btn btn-primary" onClick={downloadGraph} disabled={invalidRange || filteredReadings.length === 0 || selectedMetricSeries.length === 0}>Download PNG</button>
        </div>

        {invalidRange ? <div className="health-graph-message error">The start date must be before the end date.</div>
          : filteredReadings.length === 0 ? <div className="health-graph-message">No health readings are available in this date range.</div>
          : metricSeries.length === 0 ? <div className="health-graph-message">The readings in this date range do not contain any graphable values.</div>
          : selectedMetricSeries.length === 0 ? <div className="health-graph-message">Select at least one available graph from the Graphs dropdown.</div>
          : (
            <div className="health-graph-preview">
              <svg id="health-progress-share-card" viewBox="0 0 900 1125" xmlns="http://www.w3.org/2000/svg" role="img" aria-label={`Health progress graph for ${customer?.fullName || 'customer'}`}>
                <rect width="900" height="1125" fill="#f6f9ff" />
                <rect x="35" y="35" width="830" height="1055" rx="32" fill="#ffffff" />
                <rect x="35" y="35" width="830" height="185" rx="32" fill="#123a70" />
                <rect x="35" y="188" width="830" height="32" fill="#123a70" />
                <text x="75" y="92" fill="#a9d6ff" fontSize="22" fontFamily="Arial, sans-serif" fontWeight="700">{companyInfo?.companyName || 'CALORIES N SWEAT'}</text>
                <text x="75" y="145" fill="#ffffff" fontSize="38" fontFamily="Arial, sans-serif" fontWeight="700">{customer?.fullName || 'Health Progress'}</text>
                <text x="75" y="184" fill="#dcecff" fontSize="20" fontFamily="Arial, sans-serif">{svgDate(filteredReadings[0].readingDate)} – {svgDate(filteredReadings[filteredReadings.length - 1].readingDate)}</text>
                <text x="825" y="91" fill="#ffffff" fontSize="42" fontFamily="Arial, sans-serif" fontWeight="700" textAnchor="end">{filteredReadings.length}</text>
                <text x="825" y="120" fill="#a9d6ff" fontSize="15" fontFamily="Arial, sans-serif" textAnchor="end">READINGS</text>

                {weightChange !== null && (
                  <g>
                    <rect x="75" y="245" width="750" height="82" rx="18" fill={weightChange <= 0 ? '#ecfdf5' : '#fff7ed'} />
                    <text x="100" y="280" fill="#64748b" fontSize="16" fontFamily="Arial, sans-serif">WEIGHT CHANGE</text>
                    <text x="100" y="311" fill={weightChange <= 0 ? '#047857' : '#c2410c'} fontSize="27" fontFamily="Arial, sans-serif" fontWeight="700">{weightChange > 0 ? '+' : ''}{formatValue(weightChange, 'kg')}</text>
                    <text x="800" y="294" fill="#334155" fontSize="18" fontFamily="Arial, sans-serif" textAnchor="end">{formatValue(weightSeries.points[0].value, 'kg')}  →  {formatValue(weightSeries.points[weightSeries.points.length - 1].value, 'kg')}</text>
                  </g>
                )}

                {selectedMetricSeries.map((metric, index) => {
                  const col = index % 2;
                  const row = Math.floor(index / 2);
                  const cardX = 75 + col * 385;
                  const cardY = 355 + row * 160;
                  const first = metric.points[0].value;
                  const latest = metric.points[metric.points.length - 1].value;
                  const change = latest - first;
                  const path = buildPath(metric.points, cardX + 24, cardY + 62, 315, 45);
                  return (
                    <g key={metric.key}>
                      <rect x={cardX} y={cardY} width="365" height="145" rx="20" fill="#f8fafc" stroke="#e2e8f0" />
                      <circle cx={cardX + 26} cy={cardY + 31} r="7" fill={metric.color} />
                      <text x={cardX + 42} y={cardY + 37} fill="#334155" fontSize="18" fontFamily="Arial, sans-serif" fontWeight="700">{metric.label}</text>
                      <text x={cardX + 340} y={cardY + 39} fill="#0f172a" fontSize="24" fontFamily="Arial, sans-serif" fontWeight="700" textAnchor="end">{formatValue(latest, metric.unit)}</text>
                      <line x1={cardX + 24} y1={cardY + 85} x2={cardX + 339} y2={cardY + 85} stroke="#e2e8f0" strokeDasharray="5 5" />
                      <path d={path} fill="none" stroke={metric.color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                      {metric.points.map((point, pointIndex) => {
                        if (metric.points.length === 1) return <circle key={pointIndex} cx={cardX + 181.5} cy={cardY + 84.5} r="6" fill={metric.color} />;
                        const values = metric.points.map(item => item.value);
                        let min = Math.min(...values); let max = Math.max(...values);
                        const padding = Math.max((max - min) * 0.15, Math.abs(max || 1) * 0.03);
                        min -= padding; max += padding;
                        const px = cardX + 24 + (pointIndex / (metric.points.length - 1)) * 315;
                        const py = cardY + 107 - ((point.value - min) / (max - min || 1)) * 45;
                        return <circle key={pointIndex} cx={px} cy={py} r={pointIndex === 0 || pointIndex === metric.points.length - 1 ? 5 : 3} fill="#ffffff" stroke={metric.color} strokeWidth="3" />;
                      })}
                      <text x={cardX + 24} y={cardY + 134} fill="#64748b" fontSize="14" fontFamily="Arial, sans-serif">Start {formatValue(first, metric.unit)}</text>
                      <text x={cardX + 340} y={cardY + 134} fill={change === 0 || metric.lowerIsBetter === null ? '#64748b' : ((change < 0) === metric.lowerIsBetter ? '#047857' : '#c2410c')} fontSize="14" fontFamily="Arial, sans-serif" fontWeight="700" textAnchor="end">{change > 0 ? '+' : ''}{formatValue(change, metric.unit)}</text>
                    </g>
                  );
                })}
                <text x="450" y="1049" textAnchor="middle" fill="#64748b" fontSize="16" fontFamily="Arial, sans-serif">Consistency creates progress • Keep going!</text>
                <text x="450" y="1074" textAnchor="middle" fill="#94a3b8" fontSize="13" fontFamily="Arial, sans-serif">Progress is based on recorded health readings and is not medical advice.</text>
              </svg>
            </div>
          )}
        {downloadError && <div className="health-graph-message error">{downloadError}</div>}
        <div className="health-graph-footer"><span>{filteredReadings.length} reading{filteredReadings.length === 1 ? '' : 's'} selected</span><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
};

export default HealthProgressGraphModal;
